import AsyncCreatableSelect from "react-select/async-creatable";
import Option from "./components/Option";
import DropdownIndicator from "./components/DropdownIndicator";
import ValueContainer from "./components/ValueContainer";
import MultiValue from "./components/MultiValue";
import MenuList from "./components/MenuList";
import { GroupBase, OptionsOrGroups } from "react-select";
import { SelectItem, SelectNewOption } from "./Select";
import { classNames } from "./styles";
import { useCustomTranslation } from "../../helpers/translateHelpers";
import { useDebouncedCallback } from "use-debounce";
import { useEffect } from "react";
import { DEBOUNCE_MS } from "../../helpers/debounce";

const ariaLiveMessages = {
  guidance: () => "",
  onChange: () => "",
  onFilter: () => "",
  onFocus: () => "",
};

interface AsyncSelectCreatableProps {
  invalid?: boolean;
  loadOptions: (
    inputValue: string,
    callback: (
      options: OptionsOrGroups<SelectItem, GroupBase<SelectItem>>,
    ) => void,
  ) => void;
  value: SelectItem | SelectItem[] | null;
  onChange: (value: SelectItem | SelectItem[] | null) => void;
  isMulti?: boolean;
  placeholder?: string;
  newOption?: SelectNewOption; // Using when we want to allow user tu create new option
  multivalueTagLabelKey?: string; // Translate key to show on tag when using multivalue
  menuListFooter?: React.ReactNode;
  noOptionsText?: string;
  enableDefaultOptions?: boolean;
}

// Debounce the loadOptions callback to reduce requests while typing.
// Bypass debounce for empty input so defaultOptions are shown immediately.
type LoadOptionsCallback = (
  options: OptionsOrGroups<SelectItem, GroupBase<SelectItem>>,
) => void;

const AsyncSelectCreatable = ({
  invalid,
  loadOptions,
  onChange,
  value,
  isMulti,
  placeholder,
  menuListFooter,
  newOption,
  noOptionsText,
  multivalueTagLabelKey,
  enableDefaultOptions,
}: AsyncSelectCreatableProps) => {
  const { tInput } = useCustomTranslation();

  const debouncedLoad = useDebouncedCallback(
    (inputValue: string, callback: LoadOptionsCallback) => {
      if (!inputValue) {
        // show default options immediately
        loadOptions(inputValue, callback);
        return;
      }
      loadOptions(inputValue, callback);
    },
    DEBOUNCE_MS,
  );

  // cancel pending debounced calls on unmount
  useEffect(() => {
    return () => {
      debouncedLoad?.cancel?.();
    };
  }, [debouncedLoad]);

  return (
    <AsyncCreatableSelect
      cacheOptions
      defaultOptions={enableDefaultOptions ?? true}
      loadOptions={debouncedLoad}
      components={{
        Option: Option,
        DropdownIndicator: DropdownIndicator,
        ValueContainer: ValueContainer,
        MultiValue: MultiValue,
        MenuList: MenuList,
      }}
      classNames={classNames(invalid)}
      /* @ts-expect-error Bug in the react-select, based on documentation isMulti is boolean, but types are only false and undefined*/
      isMulti={isMulti}
      closeMenuOnSelect={!isMulti}
      hideSelectedOptions={false}
      onChange={onChange}
      value={value}
      ariaLiveMessages={ariaLiveMessages}
      screenReaderStatus={() => ""}
      placeholder={placeholder}
      noOptionsMessage={() => noOptionsText ?? tInput("select_no_options")}
      loadingMessage={() => tInput("select_loading")}
      {...{ newOption, menuListFooter, multivalueTagLabelKey }}
    />
  );
};

export default AsyncSelectCreatable;
